import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Award, BookOpen, Clock3, FileText, Loader2, X } from "lucide-react";
import axios from "../../../../axios";
import { toast } from "react-toastify";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const parseBoolean = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  String(value).toLowerCase() === "true";

const getObjectFromResponse = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (result && !Array.isArray(result) && typeof result === "object") {
    return result?.mockTest || result;
  }
  if (data && !Array.isArray(data) && typeof data === "object") {
    return data?.mockTest || data;
  }

  return null;
};

const getApiMessage = (source, fallback) =>
  source?.response?.data?.message ||
  source?.data?.message ||
  source?.message ||
  fallback;

const MyTestDetails = () => {
  const navigate = useNavigate();
  const { mockTestId } = useParams();
  const [mockTest, setMockTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPassKeyPopup, setShowPassKeyPopup] = useState(false);
  const [passKey, setPassKey] = useState("");
  const [passKeyError, setPassKeyError] = useState("");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    const fetchMockTest = async () => {
      setLoading(true);

      try {
        const response = await axios.get(`/institute/mock-tests/${mockTestId}`);
        const detail = getObjectFromResponse(response);

        if (!detail) {
          toast.error("My test details were not found");
        }

        setMockTest(detail || null);
      } catch (error) {
        toast.error(getApiMessage(error, "Unable to load my test details"));
      } finally {
        setLoading(false);
      }
    };

    fetchMockTest();
  }, [mockTestId]);

  const detail = useMemo(() => {
    const totalQuestions = pickFirst(
      mockTest?.totalQuestions,
      mockTest?.questionCount,
      mockTest?.questions?.length,
      0,
    );
    const duration = pickFirst(
      mockTest?.durationMinutes,
      mockTest?.duration,
      mockTest?.timeLimit,
      0,
    );

    return {
      title: pickFirst(mockTest?.title, mockTest?.name, mockTest?.mockTestName, "My Test"),
      description: pickFirst(mockTest?.description, mockTest?.subTitle, "My Test"),
      totalQuestions,
      duration,
      marks: pickFirst(mockTest?.totalMarks, mockTest?.marks, mockTest?.maxMarks, totalQuestions),
      isPassKeyRequired: parseBoolean(mockTest?.isPassKeyRequired),
      isSubmitted:
        mockTest?.isSubmitted ||
        mockTest?.attemptStatus === "submitted" ||
        mockTest?.status === "submitted",
    };
  }, [mockTest]);

  const startMyTest = (enteredPassKey = "", startData = null) => {
    navigate(`/dash/my-test/${mockTest?._id || mockTestId}`, {
      state: enteredPassKey
        ? { passKey: enteredPassKey, startData, startAlreadyCalled: Boolean(startData) }
        : undefined,
    });
  };

  const handlePrimaryAction = () => {
    if (detail.isSubmitted) {
      navigate(`/dash/my-test-result/${mockTest?._id || mockTestId}`);
      return;
    }

    if (detail.isPassKeyRequired) {
      setPassKey("");
      setPassKeyError("");
      setShowPassKeyPopup(true);
      return;
    }

    startMyTest();
  };

  const handlePassKeySubmit = async (event) => {
    event.preventDefault();

    const trimmedPassKey = passKey.trim();
    if (!trimmedPassKey) {
      setPassKeyError("Please enter pass key");
      return;
    }

    setStarting(true);
    setPassKeyError("");

    try {
      const response = await axios.post(
        `/institute/mock-tests/${mockTest?._id || mockTestId}/start`,
        { passKey: trimmedPassKey },
      );
      const message = getApiMessage(response, "");

      if (message) {
        toast.success(message);
      }

      setShowPassKeyPopup(false);
      startMyTest(trimmedPassKey, response?.data);
    } catch (error) {
      setPassKeyError(getApiMessage(error, "Invalid passkey"));
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfafc] pb-24 text-[#211629]">
      <main className="mx-auto max-w-5xl px-4 py-5 sm:px-6 lg:mt-14 lg:px-8">
        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center rounded-3xl bg-white shadow-sm">
            <Loader2 className="h-8 w-8 animate-spin text-[#562566]" />
          </div>
        ) : mockTest ? (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
            <div className="space-y-5">
              <section className="rounded-[1.5rem] bg-[#562566] p-4 text-left text-white shadow-[0_14px_28px_rgba(86,37,102,0.16)] sm:p-6">
                <p className="text-xs font-bold text-white/80">My Test</p>
                <h2 className="mt-1 text-2xl font-bold leading-tight sm:text-3xl">
                  {detail.title}
                </h2>
                <p className="mt-2 text-sm font-medium leading-6 text-white/82">
                  {detail.description}
                </p>

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <span className="inline-flex items-center justify-center gap-1 rounded-full bg-white/12 px-2 py-2 text-[11px] font-bold sm:gap-2 sm:text-sm">
                    <FileText className="h-4 w-4" />
                    {detail.totalQuestions} Questions
                  </span>
                  <span className="inline-flex items-center justify-center gap-1 rounded-full bg-white/12 px-2 py-2 text-[11px] font-bold sm:gap-2 sm:text-sm">
                    <Clock3 className="h-4 w-4" />
                    {detail.duration} min
                  </span>
                  <span className="inline-flex items-center justify-center gap-1 rounded-full bg-white/12 px-2 py-2 text-[11px] font-bold sm:gap-2 sm:text-sm">
                    <Award className="h-4 w-4" />
                    {detail.marks} Marks
                  </span>
                </div>
              </section>

              <section className="rounded-2xl border border-[#eee8f0] bg-white p-4 text-left shadow-sm sm:p-5">
                <h3 className="text-base font-extrabold text-[#211629]">
                  Instructions
                </h3>
                <ul className="mt-4 space-y-3 text-sm font-medium leading-6 text-[#6e6875]">
                  <li>Read each question carefully before selecting your answer.</li>
                  <li>You can review and change your answers before final submission.</li>
                  <li>Make sure to submit before the timer ends.</li>
                </ul>
              </section>
            </div>

            <button
              type="button"
              onClick={handlePrimaryAction}
              className={`hidden h-14 w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base font-bold text-white transition hover:scale-[0.98] lg:inline-flex ${
                detail.isSubmitted ? "bg-[#562566]" : "bg-[#F5A623]"
              }`}
            >
              {detail.isSubmitted ? "View Report" : "Start My Test"}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#eee8f0] bg-white p-8 text-center shadow-sm">
            <BookOpen className="mx-auto h-10 w-10 text-[#562566]" />
            <h2 className="mt-3 text-xl font-extrabold">My test not found</h2>
          </div>
        )}
      </main>

      {mockTest && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#eee8f0] px-4 py-3 backdrop-blur lg:hidden">
          <button
            type="button"
            onClick={handlePrimaryAction}
            className={`flex h-12 w-full items-center justify-center gap-2 rounded-full text-sm font-extrabold text-white shadow-[0_10px_20px_rgba(86,37,102,0.18)] ${
              detail.isSubmitted ? "bg-[#562566]" : "bg-[#F5A623]"
            }`}
          >
            {detail.isSubmitted ? "View Report" : "Start My Test"}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {showPassKeyPopup && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm"
          onClick={() => !starting && setShowPassKeyPopup(false)}
        >
          <form
            className="relative w-full max-w-md rounded-2xl bg-white p-5 text-left shadow-[0_18px_50px_rgba(33,22,41,0.24)]"
            onSubmit={handlePassKeySubmit}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => !starting && setShowPassKeyPopup(false)}
              className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#f7f2f8] text-[#562566]"
              aria-label="Close pass key popup"
            >
              <X className="h-4 w-4" />
            </button>

            <h3 className="pr-8 text-lg font-extrabold text-[#211629]">
              Enter Pass Key
            </h3>
            <p className="mt-2 text-sm font-medium leading-6 text-[#6e6875]">
              This test requires a pass key before you can start.
            </p>

            <label
              htmlFor="my-test-pass-key"
              className="mt-5 block text-sm font-bold text-[#211629]"
            >
              Pass Key
            </label>
            <input
              id="my-test-pass-key"
              type="text"
              value={passKey}
              onChange={(event) => {
                setPassKey(event.target.value);
                if (passKeyError) setPassKeyError("");
              }}
              placeholder="Enter pass key"
              className="mt-2 h-12 w-full rounded-2xl border border-[#e5dde9] px-4 text-sm font-semibold text-[#211629] outline-none focus:border-[#562566]"
              autoFocus
            />

            {passKeyError && (
              <p className="mt-2 text-sm font-semibold text-red-500">
                {passKeyError}
              </p>
            )}

            <button
              type="submit"
              disabled={starting}
              className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#F5A623] text-sm font-extrabold text-white disabled:opacity-70"
            >
              {starting ? "Checking..." : "Start My Test"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default MyTestDetails;
